import {
  type DocumentMeta,
  useSelectionManager,
} from "@prismicio/editor-fields"
import {
  AnimatedElement,
  BlankSlate,
  BlankSlateDescription,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardImage,
  DefaultErrorMessage,
  dialogErrorImage,
  ErrorBoundary,
  Skeleton,
  Text,
} from "@prismicio/editor-ui"
import { type ReactNode, Suspense, useEffect, useState } from "react"

import { emptyDocumentsList } from "../../app/constants"
import { useTeamSpace } from "../../app/services/role"
import type { Tab } from "../DocumentListHeader/DocumentListHeader"
import { useCanManageSpaces } from "../teamSpaces/TeamSpaces.utils"
import { TeamSpacesDialog } from "../teamSpaces/TeamSpacesDialog"
import { useDocumentListSearchParams } from "../useDocumentListSearchParams"
import { ActionBar } from "./ActionBar"
import { ActionBarSkeleton } from "./ActionBar/ActionBar"
import {
  CreateNewDocumentButton,
  CreateNewDocumentModal,
  type CreateNewDocumentProps,
} from "./CreateNewDocumentDialog"
import {
  DocumentListLoadingTable,
  DocumentListTable,
} from "./DocumentListTable"
import { useDocumentListData, useIsEmptyTeamSpace } from "./useDocumentListData"
import { useScrolledToPageBottom } from "./useScrolledToPageBottom"

interface DocumentListBodyProps {
  tab: Tab
  /**
   * The release id to filter the documents by.
   * If not provided, the documents will be filtered by the current release.
   *
   * This also determines if the create new document button should link the new document to the release.
   *
   * Only works for the "upcoming" tab.
   */
  releaseId?: string
  createEnabled?: boolean
}

export function DocumentListBody(props: DocumentListBodyProps) {
  const { tab, releaseId, createEnabled } = props

  const [modalOpen, setModalOpen] = useState(false)

  useClearSelectionOnTabChange({ tab })

  const {
    data: { documents },
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useDocumentListData({
    tab,
    releaseId,
    enableRevalidationToast: true,
  })

  const hasFiltersApplied = useHasFiltersApplied()

  const isEmptyTeamSpace = useIsEmptyTeamSpace(tab)

  useScrolledToPageBottom(
    () => !isEmptyTeamSpace && hasNextPage && !isFetching && fetchNextPage(),
  )

  const hasDocuments = documents.length > 0

  // Note: we want both to show the ActionBar when there are documents but also
  // when the user applied some filters and returned no results
  const showActionBar = (hasDocuments || hasFiltersApplied) && !isEmptyTeamSpace

  return (
    <>
      <Box flexDirection="column" gap={4}>
        {tab !== "upcoming" && <TeamSpaceName />}

        {showActionBar && (
          <ActionBar
            tab={tab}
            setModalOpen={setModalOpen}
            createEnabled={createEnabled}
            releaseId={releaseId}
          />
        )}
      </Box>

      <BodyAnimation tab={tab}>
        <DocumentListBodyContent
          tab={tab}
          documents={documents}
          setModalOpen={setModalOpen}
        />
      </BodyAnimation>

      {hasDocuments && !isEmptyTeamSpace && (
        <AnimatedElement animation={{ name: "fade" }}>
          {isFetchingNextPage && (
            <Text align="center" color="grey11" sx={{ marginBlock: 8 }}>
              Loading more results ...
            </Text>
          )}
        </AnimatedElement>
      )}

      <CreateNewDocumentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        releaseId={tab === "upcoming" && releaseId ? releaseId : undefined}
      />
    </>
  )
}

function TeamSpaceName() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Skeleton height={30} width={108} />}>
        <TeamSpaceNameBase />
      </Suspense>
    </ErrorBoundary>
  )
}

function TeamSpaceNameBase() {
  const { optimisticSearchParams } = useDocumentListSearchParams()
  const teamSpace = useTeamSpace(optimisticSearchParams.space)

  return (
    <Text component="h2" variant="h3">
      {teamSpace?.name || ""}
    </Text>
  )
}

interface BodyAnimationProps {
  tab: Tab
  children: ReactNode
}

function BodyAnimation(props: BodyAnimationProps) {
  const { tab, children } = props

  const { searchParams } = useDocumentListSearchParams()

  return (
    <AnimatedElement
      animation={{ name: "slide", offset: -16 }}
      enterDuration={300}
      // It looks awful with a positive exit duration as the scroll to top occurs before the animation kicks in,
      // which makes no sense in terms of continuity.
      exitDuration={0}
    >
      <Box flexDirection="column" key={`${tab}-${searchParams.space || ""}`}>
        {children}
      </Box>
    </AnimatedElement>
  )
}

interface DocumentListBodyContentProps {
  tab: Tab
  documents: DocumentMeta[]
  setModalOpen: (isOpen: boolean) => void
}

function DocumentListBodyContent(props: DocumentListBodyContentProps) {
  const { tab, documents, setModalOpen } = props

  const hasFiltersApplied = useHasFiltersApplied()

  const isEmptyTeamSpace = useIsEmptyTeamSpace(tab)

  const hasNoDocuments = documents.length === 0

  if (isEmptyTeamSpace) return <DocumentListEmptyTeamSpaceBody />

  if (hasNoDocuments && hasFiltersApplied) return <DocumentListNoResults />

  if (hasNoDocuments && tab === "working")
    return <DocumentListEmptyBody setModalOpen={setModalOpen} />

  if (hasNoDocuments && tab === "archived")
    return <ArchivedDocumentListEmptyBody />

  if (hasNoDocuments && tab === "upcoming")
    return <UpcomingDocumentListEmptyBody setModalOpen={setModalOpen} />

  return <DocumentListTable documents={documents} />
}

function useHasFiltersApplied() {
  const { searchParams } = useDocumentListSearchParams()
  const filterKeys = ["customTypes", "authors", "tags"] as const
  return filterKeys.some((key) => searchParams[key])
}

function DocumentListEmptyTeamSpaceBody() {
  const { isTransitioning } = useDocumentListSearchParams()

  const canManageSpaces = useCanManageSpaces()

  return (
    <Box opacity={isTransitioning ? 0.3 : 1}>
      <Card
        padding={0}
        sx={{ width: 498, marginTop: 80, marginInline: "auto" }}
        variant="outlined"
      >
        <CardImage src={emptyDocumentsList} height={280} />

        <CardContent padding={32}>
          <Text variant="h3">There are no pages in this Space</Text>

          <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
            There are currently no Custom Types assigned to this Space.{" "}
            {canManageSpaces
              ? "Once you assign a Custom Type to this Space you will see your pages here."
              : "Once an administrator in your repository assigns a Custom Type to this Space you will see your pages here."}
          </Text>

          {canManageSpaces && <UpdateSpaceSettingsButton />}
        </CardContent>
      </Card>
    </Box>
  )
}

function UpdateSpaceSettingsButton() {
  const { searchParams } = useDocumentListSearchParams()
  const teamSpace = useTeamSpace(searchParams.space)
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <Button size="large" onClick={() => setIsOpen(true)}>
        Update Space Settings
      </Button>
      <TeamSpacesDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        teamSpace={teamSpace}
      />
    </>
  )
}

function DocumentListNoResults() {
  const { isTransitioning } = useDocumentListSearchParams()

  return (
    <Box
      flexGrow={1}
      justifyContent="center"
      padding={{ top: 100 }}
      opacity={isTransitioning ? 0.3 : 1}
    >
      <BlankSlate>
        <BlankSlateIcon name="search" backgroundColor="grey4" size="large" />
        <BlankSlateTitle size="big">No documents found</BlankSlateTitle>
        <BlankSlateDescription>
          Consider tweaking your filters to find what you need.
        </BlankSlateDescription>
      </BlankSlate>
    </Box>
  )
}

function DocumentListEmptyBody(props: CreateNewDocumentProps) {
  const { setModalOpen } = props

  const { isTransitioning } = useDocumentListSearchParams()

  return (
    <Box opacity={isTransitioning ? 0.3 : 1}>
      <Card
        padding={0}
        sx={{ width: 498, marginTop: 80, marginInline: "auto" }}
        variant="outlined"
      >
        <CardImage src={emptyDocumentsList} height={280} />

        <CardContent padding={32}>
          <Text variant="h3">Once upon a time...</Text>

          <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
            This is the place where your stories take life
          </Text>

          <CreateNewDocumentButton setModalOpen={setModalOpen} size="large" />
        </CardContent>
      </Card>
    </Box>
  )
}

function ArchivedDocumentListEmptyBody() {
  const { isTransitioning } = useDocumentListSearchParams()

  return (
    <Box opacity={isTransitioning ? 0.3 : 1}>
      <Card
        padding={0}
        sx={{ width: 498, marginTop: 80, marginInline: "auto" }}
        variant="outlined"
      >
        <CardImage src={emptyDocumentsList} height={280} />

        <CardContent padding={32}>
          <Text variant="h3">No archived documents yet</Text>

          <Text variant="normal" color="grey11">
            Once you archive a document, it will appear here.
          </Text>
        </CardContent>
      </Card>
    </Box>
  )
}

interface UpcomingDocumentListEmptyBodyProps {
  setModalOpen: (open: boolean) => void
}

export function UpcomingDocumentListEmptyBody(
  props: UpcomingDocumentListEmptyBodyProps,
) {
  const { setModalOpen } = props

  const { isTransitioning } = useDocumentListSearchParams()

  return (
    <Box
      flexDirection="column"
      alignItems="center"
      flexGrow={1}
      padding={{
        block: 32,
      }}
      opacity={isTransitioning ? 0.3 : 1}
    >
      <Text variant="emphasized" color="grey11">
        This release is empty.
      </Text>
      <Text color="grey11" sx={{ marginBottom: 8 }}>
        Write content inside now!
      </Text>
      <CreateNewDocumentButton
        setModalOpen={setModalOpen}
        size="small"
        color="grey"
      />
    </Box>
  )
}

interface UseSelectionOnTabChangeArgs {
  tab: Tab
}

function useClearSelectionOnTabChange(args: UseSelectionOnTabChangeArgs) {
  const { tab } = args

  const { clearSelection } = useSelectionManager()
  useEffect(() => {
    clearSelection()
  }, [tab, clearSelection])
}

export function renderDocumentListBodyError() {
  return (
    <Box
      height="100%"
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching documents."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}

interface DocumentListBodySkeletonProps {
  tab: Tab
  createEnabled?: boolean
  releaseId?: string
}

export function DocumentListBodySkeleton(props: DocumentListBodySkeletonProps) {
  const { tab, createEnabled = true, releaseId } = props

  const [modalOpen, setModalOpen] = useState(false)

  const showSkeltonActionBar = tab === "upcoming" && releaseId === undefined

  return (
    <Box flexDirection="column" gap={24}>
      <Box flexDirection="column" gap={4}>
        {tab !== "upcoming" && <TeamSpaceName />}

        {showSkeltonActionBar ? (
          <ActionBarSkeleton />
        ) : (
          <ActionBar
            tab={tab}
            setModalOpen={setModalOpen}
            createEnabled={createEnabled}
            releaseId={releaseId}
          />
        )}
      </Box>

      <DocumentListLoadingTable />

      {createEnabled && !showSkeltonActionBar && (
        <CreateNewDocumentModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          releaseId={tab === "upcoming" && releaseId ? releaseId : undefined}
        />
      )}
    </Box>
  )
}
